import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  BarChart3,
  Brain,
  Clover,
  Handshake,
  LockKeyhole,
  Swords,
  TableProperties,
  TrendingUp,
  UserCircle,
} from "lucide-react";
import { buildApiUrl } from "../config/api";

const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value);
const formatPct = (value) => (Number.isFinite(value) ? `${Math.round(value * 100)}%` : "--");
const formatDecimal = (value) => (Number.isFinite(value) ? value.toFixed(1) : "--");

const ownerDisplayNameOverrides = {
  "Bat Man": "Kyle Seys",
  "Dennis Mcmeekan": "Dennis McMeekan",
};

function normalizeOwnerDisplayName(name) {
  return ownerDisplayNameOverrides[name] || name;
}

function postseasonWinsValue(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const match = value.match(/\d+/);
    return match ? Number(match[0]) : 0;
  }
  return 0;
}

function ownerKey(name) {
  return normalizeOwnerDisplayName(name || "").toLowerCase();
}

function firstName(name) {
  return (name || "").split(" ")[0] || name;
}

const transactionLeaders = [
  { owner: "Dennis McMeekan", total: 1167, y2023: 323, y2024: 412, y2025: 432 },
  { owner: "Lucas Boraas", total: 724, y2023: 238, y2024: 244, y2025: 242 },
  { owner: "Ben Swanson", total: 532, y2023: 130, y2024: 134, y2025: 268 },
  { owner: "Zane Viernow", total: 573, y2023: 116, y2024: 233, y2025: 224 },
  { owner: "Dylan Mizaur", total: 440, y2023: 126, y2024: 170, y2025: 144 },
  { owner: "Dylan Kolenda", total: 482, y2023: 179, y2024: 162, y2025: 141 },
  { owner: "Lane Weigel", total: 467, y2023: 180, y2024: 137, y2025: 150 },
  { owner: "Cole Solomon", total: 172, y2023: 91, y2024: 44, y2025: 37 },
  { owner: "Zak Larkin", total: 234, y2023: 0, y2024: 103, y2025: 131 },
  { owner: "Aaron Haskins", total: 151, y2023: 0, y2024: 0, y2025: 151 },
];

const tradeLeaders = [
  { owner: "Dylan Kolenda", completed: 27, proposed: 52, declined: 56, y2023: 14, y2024: 8, y2025: 5 },
  { owner: "Ben Swanson", completed: 23, proposed: 111, declined: 52, y2023: 6, y2024: 11, y2025: 6 },
  { owner: "Lane Weigel", completed: 19, proposed: 67, declined: 58, y2023: 4, y2024: 10, y2025: 5 },
  { owner: "Lucas Boraas", completed: 14, proposed: 65, declined: 32, y2023: 3, y2024: 9, y2025: 2 },
  { owner: "Dennis McMeekan", completed: 13, proposed: 186, declined: 43, y2023: 5, y2024: 4, y2025: 4 },
  { owner: "Zak Larkin", completed: 12, proposed: 32, declined: 41, y2023: 0, y2024: 10, y2025: 2 },
  { owner: "Dylan Mizaur", completed: 7, proposed: 35, declined: 52, y2023: 1, y2024: 2, y2025: 4 },
  { owner: "Zane Viernow", completed: 3, proposed: 82, declined: 5, y2023: 2, y2024: 1, y2025: 0 },
  { owner: "Aaron Haskins", completed: 2, proposed: 19, declined: 29, y2023: 0, y2024: 0, y2025: 2 },
  { owner: "Cole Solomon", completed: 0, proposed: 46, declined: 22, y2023: 0, y2024: 0, y2025: 0 },
];

function buildLuckRows(rawData, activeOwners) {
  const activeKeys = new Set(activeOwners.map((row) => ownerKey(row.name)));
  const byOwner = new Map();

  rawData.forEach((row) => {
    const name = normalizeOwnerDisplayName(row.owner_name);
    if (!activeKeys.has(ownerKey(name))) return;
    const wins = Number(row.regular_wins ?? 0);
    const losses = Number(row.regular_losses ?? 0);
    const games = wins + losses;
    const pf = Number(row.points_for ?? 0);
    const pa = Number(row.points_against ?? 0);
    if (!games || !pf || !pa) return;

    const current = byOwner.get(name) || {
      owner: name,
      wins: 0,
      losses: 0,
      pointsFor: 0,
      pointsAgainst: 0,
      games: 0,
      seasons: 0,
    };
    current.wins += wins;
    current.losses += losses;
    current.pointsFor += pf;
    current.pointsAgainst += pa;
    current.games += games;
    current.seasons += 1;
    byOwner.set(name, current);
  });

  return Array.from(byOwner.values())
    .map((row) => {
      const expectedWinPct = row.pointsFor ** 2 / (row.pointsFor ** 2 + row.pointsAgainst ** 2);
      const expectedWins = expectedWinPct * row.games;
      const luck = row.wins - expectedWins;
      return {
        ...row,
        expectedWins,
        luck,
        schedule: row.pointsAgainst / row.games,
        scoring: row.pointsFor / row.games,
      };
    })
    .sort((a, b) => b.luck - a.luck);
}

function bestSeasonForOwner(rawData, ownerName) {
  return rawData
    .filter((row) => ownerKey(row.owner_name) === ownerKey(ownerName))
    .map((row) => {
      const wins = Number(row.regular_wins ?? 0);
      const losses = Number(row.regular_losses ?? 0);
      const games = wins + losses;
      return {
        ...row,
        wins,
        losses,
        winPct: games ? wins / games : 0,
        postseasonWins: postseasonWinsValue(row.postseason_wins),
      };
    })
    .sort((a, b) => b.winPct - a.winPct || b.wins - a.wins || (b.points_for ?? 0) - (a.points_for ?? 0))[0];
}

function TransactionYearChip({ year, value, valueLabel }) {
  const displayValue = value == null ? "--" : formatNumber(value);
  return (
    <span className="inline-flex min-w-[3.75rem] shrink-0 flex-col overflow-hidden rounded-md border text-center tabular-nums" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
      <span className="border-b px-2 py-1 text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--table-head-text)]" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
        {year}
      </span>
      <span className="flex flex-col px-2 py-1 text-[11px] font-semibold leading-tight text-[var(--text-main)]">
        <span>{displayValue}</span>
        {valueLabel ? <span className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.06em] text-[var(--muted-text)]">{valueLabel}</span> : null}
      </span>
    </span>
  );
}

function TradeMetricChip({ label, value }) {
  return (
    <span className="inline-flex min-w-[5.75rem] shrink-0 flex-col overflow-hidden rounded-md border text-center tabular-nums" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
      <span className="border-b px-2 py-1 text-[9px] font-bold normal-case tracking-[0.08em] text-[var(--table-head-text)]" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
        {label}
      </span>
      <span className="px-2 py-1 text-[11px] font-semibold text-[var(--text-main)]">
        {formatNumber(value)}
      </span>
    </span>
  );
}

function CompactMetricChip({ label, value, emphasis = false, fullWidth = false }) {
  return (
    <span
      className={`${fullWidth ? "w-full justify-between" : ""} inline-flex min-w-0 items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold leading-none tabular-nums ${
        emphasis ? "text-[var(--text-main)]" : "text-[var(--muted-text)]"
      }`}
      style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}
    >
      <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--table-head-text)]">{label}</span>
      <span className="truncate">{value}</span>
    </span>
  );
}

function RivalryMetrics({ gap, score }) {
  return (
    <div className="mt-2 flex w-full min-w-0 overflow-hidden rounded-md border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2 border-r px-2 py-1.5" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
        <span className="shrink-0 text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--table-head-text)]">Gap</span>
        <span className="min-w-0 truncate text-[11px] font-semibold tabular-nums text-[var(--muted-text)]">{gap}</span>
      </div>
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2 px-2 py-1.5">
        <span className="shrink-0 text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--table-head-text)]">Score</span>
        <span className="min-w-0 truncate text-[11px] font-semibold tabular-nums text-[var(--text-main)]">{score}</span>
      </div>
    </div>
  );
}

function positionListSummary(positions = []) {
  return positions.map((row) => `${row.position} ${Math.round((row.rate ?? row.probability ?? 0) * 100)}%`).join(" / ");
}

function formatPositionRank(position, rank) {
  if (!position || rank == null) return "--";
  return `${position}${rank}`;
}

const POSITION_COLORS = {
  RB: "#f59e0b",
  WR: "#3b82f6",
  TE: "#22c55e",
  QB: "#ef4444",
  "D/ST": "#eab308",
  DST: "#eab308",
  DEF: "#eab308",
  K: "#9ca3af",
};

const POSITION_COLOR_LEGEND = [
  { position: "RB", label: "RB", color: POSITION_COLORS.RB },
  { position: "WR", label: "WR", color: POSITION_COLORS.WR },
  { position: "TE", label: "TE", color: POSITION_COLORS.TE },
  { position: "QB", label: "QB", color: POSITION_COLORS.QB },
  { position: "D/ST", label: "D/ST", color: POSITION_COLORS["D/ST"] },
  { position: "K", label: "K", color: POSITION_COLORS.K },
];

function getPositionColor(position) {
  return POSITION_COLORS[position] || "var(--accent-soft)";
}

const EXCLUDED_KEEPER_PLAYERS = new Set(["Puka Nacua"]);

function formatSigned(value) {
  if (!Number.isFinite(value)) return "--";
  return value > 0 ? `+${value}` : String(value);
}

function reachCellStyle(level) {
  const styles = {
    high: { background: "rgba(239, 68, 68, 0.26)", color: "#fecaca", borderColor: "rgba(239, 68, 68, 0.58)" },
    medium: { background: "rgba(245, 158, 11, 0.22)", color: "#fde68a", borderColor: "rgba(245, 158, 11, 0.52)" },
    low: { background: "rgba(59, 130, 246, 0.16)", color: "#bfdbfe", borderColor: "rgba(59, 130, 246, 0.42)" },
    none: { background: "var(--panel-soft-bg)", color: "var(--muted-text)", borderColor: "var(--border-soft)" },
  };
  return styles[level] || styles.none;
}

function heatmapCellLabel(cell) {
  if (!cell?.reach_count) return "--";
  return cell.level === "high" ? "High" : cell.level === "medium" ? "Med" : "Low";
}

function reachContextForPrediction(profile, prediction) {
  const position = prediction?.best_guess_position;
  if (!position || prediction?.keeper_occupied) return null;
  const roundRow = (profile?.reach_profile?.heatmap || []).find((row) => row.round === prediction.round);
  const cell = (roundRow?.cells || []).find((item) => item.position === position);
  if (!cell?.reach_count) return null;
  return cell;
}

function ReachContextBlock({ profile, prediction }) {
  const cell = reachContextForPrediction(profile, prediction);
  if (!cell) return null;

  return (
    <div className="mt-3 rounded-md border px-3 py-2" style={{ borderColor: "var(--border-soft)", background: "var(--highlight-bg)" }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--table-head-text)]">Reach Context</p>
        <p className="text-xs font-bold tabular-nums text-[var(--accent-soft)]">
          {prediction.best_guess_position} Reach History: {cell.reach_count} of {cell.eligible_picks}
        </p>
      </div>
      <p className="mt-1 text-xs font-semibold leading-5 text-[var(--muted-text)]">
        Avg reach {formatSigned(cell.average_reach)} picks in this round/position bucket. Watch names 5-10 ADP spots later than pick {prediction.projected_pick || "--"}.
      </p>
    </div>
  );
}

function sourceSummary(row) {
  const parts = [];
  if (row.draft_count) parts.push(`${row.draft_count} draft`);
  if (row.trade_count) parts.push(`${row.trade_count} trade`);
  return parts.join(" / ") || (row.sources || []).join(" / ") || "--";
}

function displaySeasonValue(row, year, activeYearsByOwner) {
  const activeYears = activeYearsByOwner.get(ownerKey(row.owner));
  if (activeYears && !activeYears.has(year)) return null;
  return row[`y${year}`];
}

function compareSeasonValues(a, b, year, activeYearsByOwner, desc) {
  const aValue = displaySeasonValue(a, year, activeYearsByOwner);
  const bValue = displaySeasonValue(b, year, activeYearsByOwner);
  if (aValue == null && bValue == null) return a.owner.localeCompare(b.owner);
  if (aValue == null) return 1;
  if (bValue == null) return -1;
  const result = aValue - bValue;
  return desc ? -result : result;
}

function DraftLabPanel({ initialDraftView = "predictive", lockedView = null, preloadedKeepers = null, keepersError = "" }) {
  const keepersOnly = lockedView === "keepers";
  const savedPassword = window.sessionStorage.getItem("draft-lab-password") || "";
  const [password, setPassword] = useState(savedPassword);
  const [unlocked, setUnlocked] = useState(keepersOnly || Boolean(savedPassword));
  const [draftData, setDraftData] = useState(preloadedKeepers ? { keepers: preloadedKeepers } : null);
  const [selectedManager, setSelectedManager] = useState("");
  const [activeDraftView, setActiveDraftView] = useState(initialDraftView);
  const [loadingDraft, setLoadingDraft] = useState(keepersOnly ? !preloadedKeepers : Boolean(savedPassword));
  const [accessError, setAccessError] = useState(keepersError);
  const [staleReachRefreshAttempted, setStaleReachRefreshAttempted] = useState(false);

  const fetchDraftData = (candidatePassword) => {
    const normalizedPassword = (candidatePassword || "").trim();
    setLoadingDraft(true);
    setAccessError("");
    fetch(`${buildApiUrl("/draft-trends")}?t=${Date.now()}`, {
      cache: "no-store",
      headers: { Authorization: `Bearer ${normalizedPassword}` },
    })
      .then((response) => {
        if (!response.ok) {
          window.sessionStorage.removeItem("draft-lab-password");
          setUnlocked(false);
          setAccessError("That password did not unlock Draft Lab.");
          return null;
        }
        return response.json();
      })
      .then((payload) => {
        if (!payload) return;
        setDraftData(payload.data || null);
        setUnlocked(true);
        window.sessionStorage.setItem("draft-lab-password", normalizedPassword);
      })
      .catch(() => {
        window.sessionStorage.removeItem("draft-lab-password");
        setUnlocked(false);
        setAccessError("Draft Lab could not be reached. Try again in a moment.");
      })
      .finally(() => setLoadingDraft(false));
  };

  useEffect(() => {
    if (keepersOnly) {
      if (preloadedKeepers) {
        setDraftData({ keepers: preloadedKeepers });
        setAccessError(keepersError);
        setLoadingDraft(false);
        setUnlocked(true);
        return;
      }

      setLoadingDraft(true);
      fetch(buildApiUrl("/keepers"))
        .then((response) => {
          if (!response.ok) {
            throw new Error("Keepers could not be reached.");
          }
          return response.json();
        })
        .then((payload) => {
          setDraftData({ keepers: payload.data || [] });
          setUnlocked(true);
        })
        .catch(() => {
          setDraftData({ keepers: [] });
          setAccessError("Keepers could not be reached. Try again in a moment.");
        })
        .finally(() => setLoadingDraft(false));
      return;
    }

    if (savedPassword && !draftData) {
      fetchDraftData(savedPassword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keepersOnly, preloadedKeepers, keepersError]);

  useEffect(() => {
    if (
      keepersOnly ||
      loadingDraft ||
      !draftData?.manager_profiles?.length ||
      draftData.manager_profiles.some((row) => row.reach_profile) ||
      staleReachRefreshAttempted ||
      !savedPassword
    ) {
      return;
    }

    setStaleReachRefreshAttempted(true);
    fetchDraftData(savedPassword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keepersOnly, loadingDraft, draftData, savedPassword, staleReachRefreshAttempted]);

  const managerProfiles = draftData?.manager_profiles || [];
  const activeManager = selectedManager || managerProfiles[0]?.manager || "";
  const profile = managerProfiles.find((row) => row.manager === activeManager) || managerProfiles[0];
  const reachProfile = profile?.reach_profile || {};
  const hasReachProfile = Boolean(profile?.reach_profile);
  const predictions = (draftData?.predictions || []).filter((row) => row.manager === activeManager);
  const positionRuns = (draftData?.position_runs || []).slice(-8).reverse();
  const keepers = (draftData?.keepers || []).filter((keeper) => !EXCLUDED_KEEPER_PLAYERS.has(keeper.player));

  const handleSubmit = (event) => {
    event.preventDefault();
    fetchDraftData(password);
  };

  if (!unlocked) {
    return (
      <div className="mx-auto max-w-md rounded-lg border p-5" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-[var(--nav-active-text)]" style={{ background: "var(--nav-active-bg)" }}>
            <LockKeyhole size={20} />
          </div>
          <div>
            <p className="text-sm font-bold text-[var(--text-main)]">{lockedView === "keepers" ? "Keepers Access" : "Draft Lab Access"}</p>
            <p className="text-xs font-semibold text-[var(--muted-text)]">Enter the league password.</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3">
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="w-full rounded-md border px-3 py-2 text-base font-semibold outline-none sm:text-sm"
            style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)", color: "var(--text-main)" }}
            autoComplete="current-password"
          />
          <button
            type="submit"
            disabled={!password.trim() || loadingDraft}
            className="inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-60"
            style={{ background: "var(--nav-active-bg)", color: "var(--nav-active-text)" }}
          >
            <LockKeyhole size={16} />
            {loadingDraft ? "Checking" : "Unlock"}
          </button>
          {accessError ? (
            <p className="rounded-md border px-3 py-2 text-xs font-semibold" style={{ borderColor: "var(--border-soft)", color: "var(--muted-text)", background: "var(--panel-soft-bg)" }}>
              {accessError}
            </p>
          ) : null}
        </form>
      </div>
    );
  }

  if (loadingDraft || !draftData) {
    return (
      <div className="flex min-h-[260px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--accent-soft)] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {activeDraftView !== "keepers" ? (
        <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-strong-bg)" }}>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Active Draft Profile</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {!lockedView ? (
            <div className="inline-flex rounded-md border p-1" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
            {[
              { id: "keepers", label: "Keepers" },
              { id: "predictive", label: "Predictive" },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveDraftView(item.id)}
                className={`rounded px-3 py-1.5 text-xs font-bold ${activeDraftView === item.id ? "text-[var(--nav-active-text)]" : "text-[var(--muted-text)]"}`}
                style={activeDraftView === item.id ? { background: "var(--nav-active-bg)" } : {}}
              >
                {item.label}
              </button>
            ))}
            </div>
          ) : null}
            <select
              value={activeManager}
              onChange={(event) => setSelectedManager(event.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm font-semibold outline-none sm:w-auto"
              style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)", color: "var(--text-main)" }}
            >
              {managerProfiles.map((row) => (
                <option key={row.manager} value={row.manager} style={{ color: "#111111", background: "#ffffff" }}>
                  {row.manager}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}

      {activeDraftView === "keepers" && (
        <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
          <div className="hidden grid-cols-[58px,1fr,1fr,86px,96px,118px,118px,86px] border-b px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[var(--table-head-text)] lg:grid" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
            <span></span>
            <span>Manager</span>
            <span>Keeper</span>
            <span className="text-center">Round 1</span>
            <span className="text-center">2025 Rank</span>
            <span className="text-center">2025 Points</span>
            <span className="text-center">2026 Proj</span>
            <span className="text-center">ADP</span>
          </div>
          <div className="divide-y lg:hidden" style={{ borderColor: "var(--border-soft)" }}>
            {keepers.map((keeper) => (
              <div key={`${keeper.manager}-${keeper.player}`} className="px-4 py-3.5">
                <div className="flex flex-col items-center gap-3 text-center">
                  <img
                    src={keeper.headshot_url}
                    alt=""
                    className="h-16 w-16 rounded-md object-cover"
                    onError={(event) => { event.currentTarget.style.visibility = "hidden"; }}
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-[var(--text-main)]">{keeper.player}</p>
                    <p className="mt-1 text-xs font-semibold text-[var(--muted-text)]">{keeper.manager} · Pick {keeper.occupied_pick || "--"}</p>
                    <p className="mt-1 text-xs font-semibold text-[var(--muted-text)]">2025 Rank · {formatPositionRank(keeper.position, keeper.previous_year_position_rank)}</p>
                  </div>
                  <p className="text-sm font-bold text-[var(--accent-soft)]">{keeper.position}</p>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <CompactMetricChip label="2025" value={keeper.last_year_points != null ? formatDecimal(Number(keeper.last_year_points)) : "--"} fullWidth />
                  <CompactMetricChip label="Proj" value={keeper.projected_points != null ? formatDecimal(Number(keeper.projected_points)) : "--"} fullWidth />
                  <CompactMetricChip label="ADP" value={keeper.adp != null ? formatDecimal(Number(keeper.adp)) : "--"} fullWidth />
                </div>
              </div>
            ))}
          </div>
          {keepers.map((keeper) => (
            <div key={`${keeper.manager}-${keeper.player}`} className="hidden grid-cols-[58px,1fr,1fr,86px,96px,118px,118px,86px] items-center border-b px-4 py-3 last:border-b-0 lg:grid" style={{ borderColor: "var(--border-soft)" }}>
              <img
                src={keeper.headshot_url}
                alt=""
                className="h-11 w-11 rounded-md object-cover"
                onError={(event) => { event.currentTarget.style.visibility = "hidden"; }}
              />
              <p className="truncate text-sm font-bold text-[var(--text-main)]">{keeper.manager}</p>
              <p className="truncate text-sm font-bold text-[var(--text-main)]">{keeper.player}</p>
              <p className="text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">Pick {keeper.occupied_pick || "--"}</p>
              <p className="text-center text-sm font-semibold tabular-nums text-[var(--table-text)]">{formatPositionRank(keeper.position, keeper.previous_year_position_rank)}</p>
              <p className="text-center text-sm font-semibold tabular-nums text-[var(--table-text)]">{keeper.last_year_points != null ? formatDecimal(Number(keeper.last_year_points)) : "--"}</p>
              <p className="text-center text-sm font-semibold tabular-nums text-[var(--table-text)]">{keeper.projected_points != null ? formatDecimal(Number(keeper.projected_points)) : "--"}</p>
              <p className="text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">{keeper.adp != null ? formatDecimal(Number(keeper.adp)) : "--"}</p>
            </div>
          ))}
        </div>
      )}

      {activeDraftView === "predictive" && profile ? (
        <>
          <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
            <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted-text)] sm:text-xs sm:tracking-[0.18em]">Draft Seasons</p>
              <p className="mt-3 text-xl font-bold text-[var(--text-main)] sm:text-2xl">{profile.draft_seasons}</p>
            </div>
            <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted-text)] sm:text-xs sm:tracking-[0.18em]">Draft Position</p>
              <p className="mt-3 text-xl font-bold text-[var(--text-main)] sm:text-2xl">{profile.current_draft_position || "--"}</p>
            </div>
            <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted-text)] sm:text-xs sm:tracking-[0.18em]">Total Picks</p>
              <p className="mt-3 text-xl font-bold text-[var(--text-main)] sm:text-2xl">{formatNumber(profile.total_picks)}</p>
            </div>
            <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted-text)] sm:text-xs sm:tracking-[0.18em]">Years</p>
              <p className="mt-3 text-sm font-bold leading-6 text-[var(--text-main)]">{(profile.years || []).join(", ") || "--"}</p>
            </div>
          </div>

          <div className="grid gap-5 xl:grid-cols-[1fr,1fr]">
            <div className="space-y-5">
              <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
                <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                  <TableProperties size={16} className="text-[var(--accent-soft)]" />
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Manager Tendencies</p>
                </div>
                <div className="grid grid-cols-3 gap-1.5 border-b px-4 py-3 sm:flex sm:flex-wrap sm:gap-2" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
                  {POSITION_COLOR_LEGEND.map((item) => (
                    <div key={item.position} className="inline-flex min-w-0 items-center justify-center gap-1 rounded-md border px-1.5 py-1 sm:gap-1.5 sm:px-2" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: item.color }} />
                      <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--muted-text)]">{item.label}</span>
                    </div>
                  ))}
                </div>
                <div className="divide-y" style={{ borderColor: "var(--border-soft)" }}>
                  {(profile.early_rounds || []).map((row) => (
                    <div key={row.round} className="grid grid-cols-[72px,1fr] gap-3 px-4 py-3">
                      <p className="text-sm font-bold text-[var(--accent-soft)]">Round {row.round}</p>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[var(--text-main)]">{positionListSummary(row.positions)}</p>
                        <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-black/25">
                          {row.positions.map((position) => (
                            <span key={position.position} style={{ width: `${Math.max(8, Math.round(position.rate * 100))}%`, background: getPositionColor(position.position) }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!profile.early_rounds?.length ? (
                    <div className="px-4 py-6 text-sm font-semibold text-[var(--muted-text)]">No completed draft history for this manager yet.</div>
                  ) : null}
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
                <div className="flex items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                  <div className="flex items-center gap-2">
                    <TrendingUp size={16} className="text-[var(--accent-soft)]" />
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Reach Heatmap</p>
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--muted-text)]">ADP +5</p>
                </div>
                <div className="grid grid-cols-2 gap-2 border-b px-4 py-3 sm:grid-cols-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
                  <CompactMetricChip label="Reach %" value={formatPct(reachProfile.reach_rate)} fullWidth />
                  <CompactMetricChip label="Avg Reach" value={formatSigned(reachProfile.average_reach)} fullWidth />
                  <CompactMetricChip label="Favorite" value={reachProfile.favorite_reach_position || "--"} fullWidth />
                  <CompactMetricChip label="ADP Matches" value={`${reachProfile.matched_picks || 0}/${reachProfile.eligible_picks || 0}`} fullWidth />
                </div>
                {!hasReachProfile ? (
                  <div className="px-4 py-6 text-sm font-semibold leading-6 text-[var(--muted-text)]">
                    Reach profile is not present in this Draft Lab API response. Backend/cache needs the reach-profile update before this graph can render.
                  </div>
                ) : (reachProfile.heatmap || []).length ? (
                  <div className="px-4 py-3">
                    <div className="grid grid-cols-[58px,repeat(4,minmax(0,1fr))] gap-1.5">
                      <span />
                      {["RB", "WR", "QB", "TE"].map((position) => (
                        <span key={position} className="text-center text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--table-head-text)]">{position}</span>
                      ))}
                      {(reachProfile.heatmap || []).map((row) => (
                        <React.Fragment key={row.round}>
                          <span className="flex h-9 items-center text-xs font-bold text-[var(--accent-soft)]">R{row.round}</span>
                          {(row.cells || []).map((cell) => (
                            <div
                              key={`${row.round}-${cell.position}`}
                              className="flex h-9 min-w-0 items-center justify-center rounded-md border px-1 text-[10px] font-bold uppercase tracking-[0.04em] tabular-nums"
                              title={`${cell.position} round ${row.round}: ${cell.reach_count || 0}/${cell.eligible_picks || 0} reaches, avg ${formatSigned(cell.average_reach)}`}
                              style={reachCellStyle(cell.level)}
                            >
                              {heatmapCellLabel(cell)}
                            </div>
                          ))}
                        </React.Fragment>
                      ))}
                    </div>
                    {reachProfile.biggest_reach ? (
                      <p className="mt-3 text-xs font-semibold leading-5 text-[var(--muted-text)]">
                        Biggest reach: {reachProfile.biggest_reach.year} - {reachProfile.biggest_reach.player} ({[reachProfile.biggest_reach.position, reachProfile.biggest_reach.team].filter(Boolean).join(" - ")}) at pick {reachProfile.biggest_reach.overall_pick}, ADP {formatDecimal(Number(reachProfile.biggest_reach.fantasydata_adp))}, {formatSigned(reachProfile.biggest_reach.reach_delta)}
                      </p>
                    ) : (
                      <p className="mt-3 text-xs font-semibold text-[var(--muted-text)]">No early-round reaches found from matched FantasyData ADP.</p>
                    )}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-sm font-semibold text-[var(--muted-text)]">No matched early-round ADP history for this manager yet.</div>
                )}
              </div>

              <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
                <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                  <UserCircle size={16} className="text-[var(--accent-soft)]" />
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Recent Player Bias - Last 6 Years</p>
                </div>
                {(profile.recent_player_bias || []).length ? (
                  <div className="divide-y" style={{ borderColor: "var(--border-soft)" }}>
                    {(profile.recent_player_bias || []).map((row) => (
                      <div key={`${row.player}-${row.years?.join("-")}`} className="grid grid-cols-[1fr,72px] gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-[var(--text-main)]">
                            {row.player}
                            {row.position ? <span className="ml-2 text-xs font-bold text-[var(--accent-soft)]">{row.position}</span> : null}
                          </p>
                          <p className="mt-1 text-xs font-semibold text-[var(--muted-text)]">
                            {(row.years || []).join(", ")} · {sourceSummary(row)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold tabular-nums text-[var(--text-main)]">{row.appearances}</p>
                          <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--muted-text)]">Times</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-6 text-sm font-semibold leading-6 text-[var(--muted-text)]">
                    No repeat drafted/traded players found in the last 6 years.
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
              <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                <Brain size={16} className="text-[var(--accent-soft)]" />
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Predictive Board</p>
              </div>
              <div>
                {predictions.map((row) => (
                  <div
                    key={`${row.manager}-${row.round}`}
                    className="border-b-4 px-4 py-3 last:border-b-0"
                    style={{
                      borderBottomColor: "var(--border-strong)",
                      background: row.keeper_occupied ? "var(--highlight-bg)" : "transparent",
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-[var(--text-main)]">
                          Round {row.round}
                          {row.keeper_occupied ? (
                            <span className="ml-2 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]" style={{ background: "var(--pill-bg)", color: "var(--pill-text)" }}>
                              Keeper Round
                            </span>
                          ) : null}
                        </p>
                        <p className="mt-1 text-xs font-semibold text-[var(--muted-text)]">
                          {row.keeper_occupied
                            ? `Keeper locked: ${row.best_guess?.player || "Round 1 keeper"}`
                            : row.best_guess
                              ? `${row.best_guess.player} (${row.best_guess_position}, ADP ${row.best_guess.adp?.toFixed ? row.best_guess.adp.toFixed(1) : row.best_guess.adp})`
                              : "No player guess"}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-xs font-bold uppercase tracking-[0.08em] text-[var(--muted-text)]">Pick {row.projected_pick || "--"}</p>
                        {row.best_guess_probability ? (
                          <p className="mt-1 text-xs font-bold tabular-nums text-[var(--accent-soft)]">{Math.round(row.best_guess_probability * 100)}% pos fit</p>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2">
                      {row.positions.map((position) => (
                        <div key={position.position} className="rounded-md border p-2.5" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-bold text-[var(--text-main)]">{position.position}</p>
                            <p className="text-xs font-bold tabular-nums text-[var(--accent-soft)]">{Math.round(position.probability * 100)}%</p>
                          </div>
                          <p className="mt-1 text-xs font-semibold leading-5 text-[var(--muted-text)]">
                            {(position.adp_targets || []).slice(0, 3).map((target) => `${target.player} (${target.adp ? target.adp.toFixed(1) : target.rank})`).join(", ") || "No ADP match"}
                          </p>
                        </div>
                      ))}
                    </div>
                    <ReachContextBlock profile={profile} prediction={row} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-5 xl:grid-cols-[1fr,1fr]">
            <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
              <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                <TrendingUp size={16} className="text-[var(--accent-soft)]" />
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Recent Position Runs</p>
              </div>
              {positionRuns.map((run) => (
                <div key={`${run.year}-${run.round}-${run.position}`} className="border-b px-4 py-3 last:border-b-0" style={{ borderColor: "var(--border-soft)" }}>
                  <p className="text-sm font-bold text-[var(--text-main)]">
                    {run.year} Round {run.round}: {run.count}/{run.total} {run.position}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-[var(--accent-soft)]">
                    {Math.round((run.rate || 0) * 100)}% of the round
                  </p>
                  <p className="mt-1 truncate text-xs font-semibold text-[var(--muted-text)]">{(run.players || []).join(", ")}</p>
                </div>
              ))}
            </div>

            <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
              <div className="flex items-center gap-2 border-b px-4 py-3" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                <Activity size={16} className="text-[var(--accent-soft)]" />
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Latest Draft Picks</p>
              </div>
              {(profile.recent_picks || []).map((pick) => (
                <div key={`${pick.year}-${pick.overall_pick}-${pick.player_id}`} className="grid grid-cols-[64px,1fr,54px] gap-3 border-b px-4 py-3 last:border-b-0" style={{ borderColor: "var(--border-soft)" }}>
                  <p className="text-sm font-bold tabular-nums text-[var(--accent-soft)]">{pick.year}</p>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-[var(--text-main)]">{pick.player}</p>
                    <p className="text-xs font-semibold text-[var(--muted-text)]">Round {pick.round_num}, pick {pick.round_pick}</p>
                  </div>
                  <p className="text-right text-sm font-bold text-[var(--text-main)]">{pick.position}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function AnalyticsPreview({ stats = [], rawData = [], rivalries = [] }) {
  const [activeView, setActiveView] = useState("transactions");
  const [transactionSort, setTransactionSort] = useState({ key: "total", desc: true });
  const [tradeSort, setTradeSort] = useState({ key: "completed", desc: true });
  const [preloadedKeepers, setPreloadedKeepers] = useState(null);
  const [keepersError, setKeepersError] = useState("");
  const activeOwners = useMemo(
    () =>
      stats
        .filter((row) => row.is_active)
        .map((row) => ({ ...row, name: normalizeOwnerDisplayName(row.name) }))
        .sort((a, b) => (b.regular_wins ?? 0) - (a.regular_wins ?? 0)),
    [stats]
  );
  const normalizedRawData = useMemo(
    () => rawData.map((row) => ({ ...row, owner_name: normalizeOwnerDisplayName(row.owner_name) })),
    [rawData]
  );
  const activeYearsByOwner = useMemo(() => {
    const yearsByOwner = new Map();
    normalizedRawData.forEach((row) => {
      const key = ownerKey(row.owner_name);
      if (!key || !row.year) return;
      const years = yearsByOwner.get(key) || new Set();
      years.add(Number(row.year));
      yearsByOwner.set(key, years);
    });
    return yearsByOwner;
  }, [normalizedRawData]);
  const [selectedOwner, setSelectedOwner] = useState("");

  useEffect(() => {
    let cancelled = false;

    fetch(buildApiUrl("/keepers"))
      .then((response) => {
        if (!response.ok) {
          throw new Error("Keepers could not be reached.");
        }
        return response.json();
      })
      .then((payload) => {
        if (cancelled) return;
        setPreloadedKeepers(payload.data || []);
        setKeepersError("");
      })
      .catch(() => {
        if (cancelled) return;
        setPreloadedKeepers([]);
        setKeepersError("Keepers could not be reached. Try again in a moment.");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const maxTransactions = Math.max(...transactionLeaders.map((row) => row.total));
  const totalTransactions = transactionLeaders.reduce((sum, row) => sum + row.total, 0);
  const maxTrades = Math.max(...tradeLeaders.map((row) => row.completed));
  const totalTrades = tradeLeaders.reduce((sum, row) => sum + row.completed, 0);
  const rivalryRows = useMemo(
    () =>
      rivalries
        .map((row) => ({
          ...row,
          manager_a: normalizeOwnerDisplayName(row.manager_a),
          manager_b: normalizeOwnerDisplayName(row.manager_b),
          leader: normalizeOwnerDisplayName(row.leader),
        }))
        .sort((a, b) => (b.rivalry_score ?? 0) - (a.rivalry_score ?? 0))
        .slice(0, 10),
    [rivalries]
  );
  const luckRows = useMemo(() => buildLuckRows(normalizedRawData, activeOwners), [normalizedRawData, activeOwners]);
  const profileOwner = selectedOwner || activeOwners[0]?.name || "";
  const profile = activeOwners.find((row) => row.name === profileOwner) || activeOwners[0];
  const profileBestSeason = useMemo(
    () => bestSeasonForOwner(normalizedRawData, profileOwner),
    [normalizedRawData, profileOwner]
  );
  const profileTransactions = transactionLeaders.find((row) => ownerKey(row.owner) === ownerKey(profileOwner));
  const profileTrades = tradeLeaders.find((row) => ownerKey(row.owner) === ownerKey(profileOwner));
  const sortedTransactionLeaders = useMemo(() => {
    return [...transactionLeaders].sort((a, b) => {
      if (transactionSort.key === "owner") {
        const result = a.owner.localeCompare(b.owner);
        return transactionSort.desc ? -result : result;
      }
      const yearMatch = transactionSort.key.match(/^y(\d{4})$/);
      if (yearMatch) {
        return compareSeasonValues(a, b, Number(yearMatch[1]), activeYearsByOwner, transactionSort.desc);
      }
      const result = (a[transactionSort.key] ?? 0) - (b[transactionSort.key] ?? 0);
      return transactionSort.desc ? -result : result;
    });
  }, [transactionSort, activeYearsByOwner]);
  const sortedTradeLeaders = useMemo(() => {
    return [...tradeLeaders].sort((a, b) => {
      if (tradeSort.key === "owner") {
        const result = a.owner.localeCompare(b.owner);
        return tradeSort.desc ? -result : result;
      }
      const yearMatch = tradeSort.key.match(/^y(\d{4})$/);
      if (yearMatch) {
        return compareSeasonValues(a, b, Number(yearMatch[1]), activeYearsByOwner, tradeSort.desc);
      }
      const result = (a[tradeSort.key] ?? 0) - (b[tradeSort.key] ?? 0);
      return tradeSort.desc ? -result : result;
    });
  }, [tradeSort, activeYearsByOwner]);
  const updateTransactionSort = (key) => {
    setTransactionSort((current) => ({
      key,
      desc: current.key === key ? !current.desc : key !== "owner",
    }));
  };
  const updateTradeSort = (key) => {
    setTradeSort((current) => ({
      key,
      desc: current.key === key ? !current.desc : key !== "owner",
    }));
  };
  const renderSortLabel = (label, key) => {
    if (transactionSort.key !== key) return label;
    return `${label} ${transactionSort.desc ? "🔽" : "🔼"}`;
  };
  const renderTradeSortLabel = (label, key) => {
    if (tradeSort.key !== key) return label;
    return `${label} ${tradeSort.desc ? "🔽" : "🔼"}`;
  };
  const renderCompletedTradeSortLabel = () => (
    <>
      <span>{`Completed${tradeSort.key === "completed" ? ` ${tradeSort.desc ? "🔽" : "🔼"}` : ""}`}</span>
      <span className="text-[9px] tracking-[0.08em] text-[var(--muted-text)] sm:text-[10px]">Past 3 Years</span>
    </>
  );
  const renderTradeTwoLineSortLabel = (label, key) => (
    <>
      <span>{`${label}${tradeSort.key === key ? ` ${tradeSort.desc ? "🔽" : "🔼"}` : ""}`}</span>
      <span className="text-[9px] tracking-[0.08em] text-[var(--muted-text)] sm:text-[10px]">Past 3 Years</span>
    </>
  );
  const renderTradeYearSortLabel = (year, key) => (
    <>
      <span>{`${year}${tradeSort.key === key ? ` ${tradeSort.desc ? "🔽" : "🔼"}` : ""}`}</span>
      <span className="text-[9px] tracking-[0.08em] text-[var(--muted-text)] sm:text-[10px]">Completed Trades</span>
    </>
  );
  const dashboardNavItems = [
    { id: "transactions", label: "Transactions", icon: Activity },
    { id: "trades", label: "Trades", icon: Handshake },
    { id: "rivalries", label: "Rivalries", icon: Swords },
    { id: "luck", label: "Luck", icon: Clover },
    { id: "keepers", label: "Keepers", icon: LockKeyhole },
    { id: "profiles", label: "Manager Profiles", icon: UserCircle },
    { id: "draftLab", label: "Draft Lab", icon: LockKeyhole },
  ];
  const viewTitle = {
    transactions: "Transaction Activity",
    trades: "Trade Activity",
    rivalries: "Rivalries",
    luck: "Luck",
    keepers: "Keepers",
    profiles: "Manager Profiles",
    draftLab: "Draft Lab",
  }[activeView];

  const kpiCards = [
    { label: "Total Transactions - Past 3 Years", value: formatNumber(totalTransactions), icon: Activity, view: "transactions" },
    { label: "Completed Trades - Past 3 Years", value: formatNumber(totalTrades), icon: Handshake, view: "trades" },
    { label: "Top Rivalry Games", value: rivalryRows[0]?.games ?? "--", icon: Swords, view: "rivalries" },
    { label: "Luckiest Manager", value: luckRows[0]?.owner || "--", icon: Clover, view: "luck" },
  ];

  return (
    <section className="overflow-hidden rounded-lg border text-[var(--text-main)] shadow-2xl shadow-black/20" style={{ borderColor: "var(--border-soft)", background: "var(--table-shell-bg)" }}>
      <div className="grid min-h-[auto] lg:min-h-[660px] lg:grid-cols-[260px,1fr]">
        <aside className="border-b px-2.5 py-2.5 sm:px-5 sm:py-5 lg:border-b-0 lg:border-r" style={{ borderColor: "var(--border-soft)", background: "var(--panel-strong-bg)" }}>
          <div className="hidden items-center gap-3 sm:flex">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[var(--nav-active-text)] sm:h-10 sm:w-10" style={{ background: "var(--nav-active-bg)" }}>
              <BarChart3 size={21} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-tight text-[var(--text-main)]">Sunday Ledger</p>
              <p className="text-xs font-medium text-[var(--muted-text)]">League Operations</p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-1.5 pb-1 sm:mt-4 sm:gap-2 lg:mt-8 lg:block lg:space-y-1 lg:pb-0">
            {dashboardNavItems.map(({ id, label, icon: Icon }) => {
              const isActive = activeView === id;
              return (
              <button
                key={label}
                type="button"
                onClick={() => setActiveView(id)}
                className={`flex items-center gap-1.5 rounded-md px-2.5 py-2 text-left text-[11px] font-semibold sm:gap-2 sm:px-3 sm:py-2.5 sm:text-sm lg:w-full lg:gap-3 ${
                  isActive ? "text-[var(--nav-active-text)]" : "text-[var(--muted-text)]"
                } hover:bg-[var(--nav-hover-bg)] hover:text-[var(--text-main)]`}
                style={isActive ? { background: "var(--nav-active-bg)" } : {}}
              >
                <Icon className="shrink-0" size={15} />
                {label}
              </button>
            );
            })}
          </div>
        </aside>

        <div className="min-w-0 px-2.5 py-3 sm:px-5 sm:py-5 lg:px-7 lg:py-6">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b pb-3 sm:pb-5" style={{ borderColor: "var(--border-soft)" }}>
            <div>
              <h2 className="text-lg font-bold tracking-normal text-[var(--text-main)] sm:text-2xl lg:text-3xl">
                {viewTitle}
          {activeView === "transactions" || activeView === "trades" ? (
            <span className="ml-1 text-xs font-normal text-[var(--muted-text)] sm:text-sm">
              - Past Three Years{activeView === "transactions" ? " (Adds/Drops)" : ""}
            </span>
          ) : activeView === "rivalries" ? (
            <span className="ml-1 text-xs font-normal text-[var(--muted-text)] sm:text-sm">- All Time</span>
          ) : null}
              </h2>
            </div>
          </div>

          <div className="hidden gap-2 overflow-x-auto py-3 ios-scroll sm:grid sm:grid-cols-2 sm:gap-3 sm:py-5 xl:grid-cols-4">
            {kpiCards.map(({ label, value, icon: Icon, view }) => (
              <button
                key={label}
                type="button"
                onClick={() => setActiveView(view)}
                className="min-w-[142px] shrink-0 rounded-lg border p-2.5 text-left transition hover:-translate-y-0.5 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--border-strong)] sm:min-w-0 sm:p-4"
                style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 text-[9px] font-bold uppercase leading-snug tracking-[0.08em] text-[var(--muted-text)] sm:text-xs sm:tracking-[0.22em]">{label}</p>
                  <Icon className="shrink-0 text-[var(--accent-soft)]" size={16} />
                </div>
                <p className="mt-2 break-words text-base font-bold leading-tight text-[var(--text-main)] sm:mt-3 sm:text-2xl">{value}</p>
              </button>
            ))}
          </div>

          {activeView === "transactions" && (
          <div>
            <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
              <div className="hidden flex-wrap items-center justify-between gap-3 border-b px-4 py-3 sm:flex" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Transaction Activity</p>
              </div>
              <div className="hidden grid-cols-[44px,1fr,132px,82px,82px,82px] border-b px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[var(--table-head-text)] lg:grid" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                <span>#</span>
                <button type="button" onClick={() => updateTransactionSort("owner")} className="cursor-pointer whitespace-nowrap text-left">
                  {renderSortLabel("Manager", "owner")}
                </button>
                <button type="button" onClick={() => updateTransactionSort("total")} className="cursor-pointer whitespace-nowrap text-center">
                  {renderSortLabel("Past 3 Years", "total")}
                </button>
                <button type="button" onClick={() => updateTransactionSort("y2025")} className="cursor-pointer whitespace-nowrap text-center">
                  {renderSortLabel("2025", "y2025")}
                </button>
                <button type="button" onClick={() => updateTransactionSort("y2024")} className="cursor-pointer whitespace-nowrap text-center">
                  {renderSortLabel("2024", "y2024")}
                </button>
                <button type="button" onClick={() => updateTransactionSort("y2023")} className="cursor-pointer whitespace-nowrap text-center">
                  {renderSortLabel("2023", "y2023")}
                </button>
              </div>
              <div className="divide-y lg:hidden" style={{ borderColor: "var(--border-soft)" }}>
                {sortedTransactionLeaders.map((row, index) => {
                  const pct = Math.max(3, Math.round((row.total / maxTransactions) * 100));
                  return (
                    <div key={row.owner} className="px-4 py-3.5" style={{ borderColor: "var(--border-soft)" }}>
                      <div className="flex items-start gap-3">
                        <p className="w-5 shrink-0 pt-0.5 text-sm font-bold tabular-nums text-[var(--accent-soft)]">{index + 1}</p>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <p className="min-w-0 truncate text-sm font-bold leading-6 text-[var(--text-main)]">{row.owner}</p>
                            <p className="shrink-0 text-lg font-bold leading-6 tabular-nums text-[var(--text-main)]">{formatNumber(row.total)}</p>
                          </div>
                          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-black/25">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: index < 3 ? "var(--accent)" : "var(--accent-soft)" }} />
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <TransactionYearChip year="2025" value={displaySeasonValue(row, 2025, activeYearsByOwner)} />
                            <TransactionYearChip year="2024" value={displaySeasonValue(row, 2024, activeYearsByOwner)} />
                            <TransactionYearChip year="2023" value={displaySeasonValue(row, 2023, activeYearsByOwner)} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {sortedTransactionLeaders.map((row, index) => {
                const pct = Math.max(3, Math.round((row.total / maxTransactions) * 100));
                return (
                  <div key={row.owner} className="hidden grid-cols-[44px,1fr,132px,82px,82px,82px] items-center border-b px-4 py-3 last:border-b-0 lg:grid" style={{ borderColor: "var(--border-soft)" }}>
                    <p className="text-sm font-bold tabular-nums text-[var(--accent-soft)]">{index + 1}</p>
                    <div className="min-w-0 pr-4">
                      <p className="truncate text-sm font-bold text-[var(--text-main)]">{row.owner}</p>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-black/25">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: index < 3 ? "var(--accent)" : "var(--accent-soft)" }} />
                      </div>
                    </div>
                    <p className="text-center text-sm font-bold tabular-nums text-[var(--table-text)]">{formatNumber(row.total)}</p>
                    <p className="text-center text-sm font-semibold tabular-nums text-[var(--table-text)]">{displaySeasonValue(row, 2025, activeYearsByOwner) == null ? "--" : formatNumber(row.y2025)}</p>
                    <p className="text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">{displaySeasonValue(row, 2024, activeYearsByOwner) == null ? "--" : formatNumber(row.y2024)}</p>
                    <p className="text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">{displaySeasonValue(row, 2023, activeYearsByOwner) == null ? "--" : formatNumber(row.y2023)}</p>
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {activeView === "trades" && (
            <div>
              <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
                <div className="hidden flex-wrap items-center justify-between gap-3 border-b px-4 py-3 sm:flex" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Trade Activity</p>
                </div>
                <div className="hidden grid-cols-[32px,1fr,150px,150px,150px,118px,118px,118px] items-center border-b px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[var(--table-head-text)] lg:grid" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                  <span className="self-center text-left leading-tight">#</span>
                  <button type="button" onClick={() => updateTradeSort("owner")} className="cursor-pointer whitespace-nowrap text-left leading-tight">
                    {renderTradeSortLabel("Manager", "owner")}
                  </button>
                  <button type="button" onClick={() => updateTradeSort("completed")} className="flex w-full cursor-pointer flex-col items-center justify-center text-center leading-tight">
                    {renderCompletedTradeSortLabel()}
                  </button>
                  <button type="button" onClick={() => updateTradeSort("proposed")} className="flex w-full cursor-pointer flex-col items-center justify-center text-center leading-tight">
                    {renderTradeTwoLineSortLabel("Proposed", "proposed")}
                  </button>
                  <button type="button" onClick={() => updateTradeSort("declined")} className="flex w-full cursor-pointer flex-col items-center justify-center text-center leading-tight">
                    {renderTradeTwoLineSortLabel("Declined", "declined")}
                  </button>
                  <button type="button" onClick={() => updateTradeSort("y2025")} className="hidden w-full cursor-pointer flex-col items-center justify-center text-center leading-tight sm:flex">
                    {renderTradeYearSortLabel("2025", "y2025")}
                  </button>
                  <button type="button" onClick={() => updateTradeSort("y2024")} className="hidden w-full cursor-pointer flex-col items-center justify-center text-center leading-tight sm:flex">
                    {renderTradeYearSortLabel("2024", "y2024")}
                  </button>
                  <button type="button" onClick={() => updateTradeSort("y2023")} className="hidden w-full cursor-pointer flex-col items-center justify-center text-center leading-tight sm:flex">
                    {renderTradeYearSortLabel("2023", "y2023")}
                  </button>
                </div>
                <div className="divide-y lg:hidden" style={{ borderColor: "var(--border-soft)" }}>
                  {sortedTradeLeaders.map((row, index) => {
                    const pct = Math.max(3, Math.round((row.completed / maxTrades) * 100));
                    return (
                      <div key={row.owner} className="px-4 py-3.5" style={{ borderColor: "var(--border-soft)" }}>
                        <div className="flex items-start gap-3">
                          <p className="w-5 shrink-0 pt-0.5 text-sm font-bold tabular-nums text-[var(--accent-soft)]">{index + 1}</p>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <p className="min-w-0 truncate text-sm font-bold leading-6 text-[var(--text-main)]">{row.owner}</p>
                              <p className="shrink-0 text-lg font-bold leading-6 tabular-nums text-[var(--text-main)]">{formatNumber(row.completed)}</p>
                            </div>
                            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-black/25">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: index < 3 ? "var(--accent)" : "var(--accent-soft)" }} />
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              <TradeMetricChip label="Proposed" value={row.proposed} />
                              <TradeMetricChip label="Declined" value={row.declined} />
                              <div className="flex basis-full gap-1.5">
                                <TransactionYearChip year="2025" value={displaySeasonValue(row, 2025, activeYearsByOwner)} valueLabel="Accepted" />
                                <TransactionYearChip year="2024" value={displaySeasonValue(row, 2024, activeYearsByOwner)} valueLabel="Accepted" />
                                <TransactionYearChip year="2023" value={displaySeasonValue(row, 2023, activeYearsByOwner)} valueLabel="Accepted" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {sortedTradeLeaders.map((row, index) => {
                  const pct = Math.max(3, Math.round((row.completed / maxTrades) * 100));
                  return (
                    <div key={row.owner} className="hidden grid-cols-[32px,1fr,150px,150px,150px,118px,118px,118px] items-center border-b px-4 py-3 last:border-b-0 lg:grid" style={{ borderColor: "var(--border-soft)" }}>
                      <p className="text-left text-sm font-bold tabular-nums text-[var(--accent-soft)]">{index + 1}</p>
                      <div className="min-w-0 pr-4">
                        <p className="truncate text-sm font-bold text-[var(--text-main)]">{row.owner}</p>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-black/25">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: index < 3 ? "var(--accent)" : "var(--accent-soft)" }} />
                        </div>
                      </div>
                      <p className="text-center text-sm font-bold tabular-nums text-[var(--table-text)]">{formatNumber(row.completed)}</p>
                      <p className="text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">{formatNumber(row.proposed)}</p>
                      <p className="text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">{formatNumber(row.declined)}</p>
                      <p className="hidden text-center text-sm font-semibold tabular-nums text-[var(--muted-text)] sm:block">{displaySeasonValue(row, 2025, activeYearsByOwner) == null ? "--" : formatNumber(row.y2025)}</p>
                      <p className="hidden text-center text-sm font-semibold tabular-nums text-[var(--muted-text)] sm:block">{displaySeasonValue(row, 2024, activeYearsByOwner) == null ? "--" : formatNumber(row.y2024)}</p>
                      <p className="hidden text-center text-sm font-semibold tabular-nums text-[var(--muted-text)] sm:block">{displaySeasonValue(row, 2023, activeYearsByOwner) == null ? "--" : formatNumber(row.y2023)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeView === "rivalries" && (
            <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
              <div className="hidden flex-wrap items-center justify-between gap-3 border-b px-4 py-3 sm:flex" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Top 10 Head-to-Head Rivalries</p>
              </div>
              <div className="hidden grid-cols-[44px,minmax(190px,1fr),220px,90px,160px,90px] border-b px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[var(--table-head-text)] lg:grid" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                <span>#</span>
                <span>Managers</span>
                <span className="text-center">Record</span>
                <span className="text-center">Games</span>
                <span className="text-center">Point Gap</span>
                <span className="hidden text-center sm:block">Score</span>
              </div>
              {rivalryRows.length ? (
                <>
                <div className="divide-y lg:hidden" style={{ borderColor: "var(--border-soft)" }}>
                  {rivalryRows.map((row, index) => {
                    const leaderRecord = row.leader === row.manager_a ? `${row.manager_a_wins}-${row.manager_b_wins}` : `${row.manager_b_wins}-${row.manager_a_wins}`;
                    const pointLeader = firstName((row.manager_a_points ?? 0) >= (row.manager_b_points ?? 0) ? row.manager_a : row.manager_b);
                    return (
                    <div key={`${row.manager_a}-${row.manager_b}`} className="px-4 py-3.5" style={{ borderColor: "var(--border-soft)" }}>
                      <div className="flex items-start gap-3">
                        <p className="w-5 shrink-0 pt-0.5 text-sm font-bold tabular-nums text-[var(--accent-soft)]">{index + 1}</p>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <p className="min-w-0 break-words text-sm font-bold leading-snug text-[var(--text-main)]">{row.manager_a} vs {row.manager_b}</p>
                            <p className="shrink-0 text-lg font-bold leading-6 tabular-nums text-[var(--text-main)]">{formatNumber(row.games ?? 0)}</p>
                          </div>
                          <p className="mt-1 truncate text-xs font-semibold text-[var(--muted-text)]">
                            {firstName(row.leader)} leads {leaderRecord}
                          </p>
                          <RivalryMetrics
                            gap={`Adv: ${pointLeader} ${formatNumber(Math.round(row.points_gap ?? 0))}`}
                            score={formatDecimal(row.rivalry_score ?? 0)}
                          />
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                {rivalryRows.map((row, index) => (
                  <div key={`${row.manager_a}-${row.manager_b}`} className="hidden grid-cols-[44px,minmax(190px,1fr),220px,90px,160px,90px] items-center border-b px-4 py-3 last:border-b-0 lg:grid" style={{ borderColor: "var(--border-soft)" }}>
                    <p className="text-sm font-bold tabular-nums text-[var(--accent-soft)]">{index + 1}</p>
                    <div className="min-w-0 pr-4">
                      <p className="truncate text-sm font-bold text-[var(--text-main)]">{row.manager_a} vs {row.manager_b}</p>
                    </div>
                    <p className="whitespace-nowrap text-center text-xs font-semibold tabular-nums text-[var(--table-text)] sm:text-sm">
                      Leader: {row.leader} ({row.leader === row.manager_a ? `${row.manager_a_wins}-${row.manager_b_wins}` : `${row.manager_b_wins}-${row.manager_a_wins}`})
                    </p>
                    <p className="text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">{formatNumber(row.games ?? 0)}</p>
                    <p className="whitespace-nowrap text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">
                      +{firstName((row.manager_a_points ?? 0) >= (row.manager_b_points ?? 0) ? row.manager_a : row.manager_b)} ({formatNumber(Math.round(row.points_gap ?? 0))})
                    </p>
                    <p className="hidden text-center text-sm font-bold tabular-nums text-[var(--table-text)] sm:block">{formatDecimal(row.rivalry_score ?? 0)}</p>
                  </div>
                ))
                }
                </>
              ) : (
                <div className="px-4 py-6 text-sm font-semibold text-[var(--muted-text)]">
                  Rivalry data will appear after the next cache refresh.
                </div>
              )}
            </div>
          )}

          {activeView === "luck" && (
            <div className="overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
              <div className="hidden flex-wrap items-center justify-between gap-3 border-b px-4 py-3 sm:flex" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Luck Score</p>
              </div>
              <div className="hidden grid-cols-[44px,1fr,118px,132px,118px,150px,150px] border-b px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[var(--table-head-text)] lg:grid" style={{ borderColor: "var(--border-soft)", background: "var(--table-head-bg)" }}>
                <span>#</span>
                <span>Manager</span>
                <span className="text-center">Actual Wins</span>
                <span className="text-center">Expected Wins</span>
                <span className="text-center">Win +/-</span>
                <span className="text-center">Points For/Game</span>
                <span className="hidden text-center sm:block">Points Against/Game</span>
              </div>
              <div className="divide-y lg:hidden" style={{ borderColor: "var(--border-soft)" }}>
                {luckRows.map((row, index) => (
                  <div key={row.owner} className="px-4 py-3.5" style={{ borderColor: "var(--border-soft)" }}>
                    <div className="flex items-start gap-3">
                      <p className="w-5 shrink-0 pt-0.5 text-sm font-bold tabular-nums text-[var(--accent-soft)]">{index + 1}</p>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold leading-6 text-[var(--text-main)]">{row.owner}</p>
                            <p className="truncate text-xs font-semibold text-[var(--muted-text)]">{row.seasons} seasons</p>
                          </div>
                          <div className="flex shrink-0 flex-col items-end">
                            <p className={`text-lg font-bold leading-6 tabular-nums ${row.luck >= 0 ? "text-[var(--accent-soft)]" : "text-[var(--muted-text)]"}`}>
                              {row.luck > 0 ? "+" : ""}{formatDecimal(row.luck)}
                            </p>
                            <span className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.06em] text-[var(--muted-text)]">Wins +/- Expected</span>
                          </div>
                        </div>
                        <div className="mt-2 grid gap-1.5">
                          <CompactMetricChip label="Wins" value={formatDecimal(row.wins)} emphasis fullWidth />
                          <CompactMetricChip label="Expected" value={formatDecimal(row.expectedWins)} fullWidth />
                          <CompactMetricChip label="PF/G" value={formatDecimal(row.scoring)} fullWidth />
                          <CompactMetricChip label="PA/G" value={formatDecimal(row.schedule)} fullWidth />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {luckRows.map((row, index) => (
                <div key={row.owner} className="hidden grid-cols-[44px,1fr,118px,132px,118px,150px,150px] items-center border-b px-4 py-3 last:border-b-0 lg:grid" style={{ borderColor: "var(--border-soft)" }}>
                  <p className="text-sm font-bold tabular-nums text-[var(--accent-soft)]">{index + 1}</p>
                  <div className="min-w-0 pr-4">
                    <p className="truncate text-sm font-bold text-[var(--text-main)]">{row.owner}</p>
                    <p className="mt-1 text-xs font-semibold text-[var(--muted-text)]">{row.seasons} seasons</p>
                  </div>
                  <p className="text-center text-sm font-semibold tabular-nums text-[var(--table-text)]">{formatDecimal(row.wins)}</p>
                  <p className="text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">{formatDecimal(row.expectedWins)}</p>
                  <p className={`text-center text-sm font-bold tabular-nums ${row.luck >= 0 ? "text-[var(--accent-soft)]" : "text-[var(--muted-text)]"}`}>
                    {row.luck > 0 ? "+" : ""}{formatDecimal(row.luck)}
                  </p>
                  <p className="text-center text-sm font-semibold tabular-nums text-[var(--muted-text)]">{formatDecimal(row.scoring)}</p>
                  <p className="hidden text-center text-sm font-semibold tabular-nums text-[var(--muted-text)] sm:block">{formatDecimal(row.schedule)}</p>
                </div>
              ))}
            </div>
          )}

          {activeView === "profiles" && (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-strong-bg)" }}>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Active Manager Profile</p>
                <select
                  value={profileOwner}
                  onChange={(event) => setSelectedOwner(event.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm font-semibold outline-none sm:w-auto"
                  style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)", color: "var(--text-main)" }}
                >
                  {activeOwners.map((owner) => (
                    <option key={owner.name} value={owner.name} style={{ color: "#111111", background: "#ffffff" }}>
                      {owner.name}
                    </option>
                  ))}
                </select>
              </div>

              {profile ? (
                <>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
                    <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted-text)] sm:text-xs sm:tracking-[0.18em]">Regular Record</p>
                      <p className="mt-3 text-xl font-bold text-[var(--text-main)] sm:text-2xl">{profile.regular_wins ?? 0}-{profile.regular_losses ?? 0}</p>
                    </div>
                    <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted-text)] sm:text-xs sm:tracking-[0.18em]">Win Rate</p>
                      <p className="mt-3 text-xl font-bold text-[var(--text-main)] sm:text-2xl">{formatPct(profile.regular_win_pct)}</p>
                    </div>
                    <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted-text)] sm:text-xs sm:tracking-[0.18em]">Appearances</p>
                      <p className="mt-3 text-xl font-bold text-[var(--text-main)] sm:text-2xl">{profile.postseason_appearances ?? 0}</p>
                    </div>
                    <div className="rounded-lg border p-3 sm:p-4" style={{ borderColor: "var(--border-soft)", background: "var(--panel-soft-bg)" }}>
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[var(--muted-text)] sm:text-xs sm:tracking-[0.18em]">Titles</p>
                      <p className="mt-3 text-xl font-bold text-[var(--text-main)] sm:text-2xl">{profile.championships ?? 0}</p>
                    </div>
                  </div>

                  <div className="grid gap-5 xl:grid-cols-[1fr,1fr]">
                    <div className="rounded-lg border p-4 sm:p-5" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Activity Profile</p>
                      <div className="mt-4 grid grid-cols-3 gap-2 text-center sm:gap-3">
                        <div className="min-w-0">
                          <p className="break-words text-xl font-bold leading-tight text-[var(--text-main)] sm:text-2xl">{formatNumber(profileTransactions?.total ?? 0)}</p>
                          <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--muted-text)] sm:text-[10px] sm:tracking-[0.14em]">Transactions</p>
                        </div>
                        <div className="min-w-0">
                          <p className="break-words text-xl font-bold leading-tight text-[var(--text-main)] sm:text-2xl">{formatNumber(profileTrades?.completed ?? 0)}</p>
                          <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--muted-text)] sm:text-[10px] sm:tracking-[0.14em]">Trades</p>
                        </div>
                        <div className="min-w-0">
                          <p className="break-words text-xl font-bold leading-tight text-[var(--text-main)] sm:text-2xl">{formatNumber(profileTrades?.proposed ?? 0)}</p>
                          <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--muted-text)] sm:text-[10px] sm:tracking-[0.14em]">Proposed</p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border p-4 sm:p-5" style={{ borderColor: "var(--border-soft)", background: "var(--table-body-bg)" }}>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--table-head-text)]">Best Season</p>
                      <p className="mt-4 text-2xl font-bold text-[var(--text-main)] sm:text-3xl">
                        {profileBestSeason ? `${profileBestSeason.wins}-${profileBestSeason.losses}` : "--"}
                      </p>
                      <p className="mt-2 text-sm font-semibold leading-6 text-[var(--muted-text)]">
                        {profileBestSeason ? `${profileBestSeason.year} · ${formatNumber(Math.round(profileBestSeason.points_for ?? 0))} PF · ${profileBestSeason.postseasonWins} playoff wins` : "No season data available"}
                      </p>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {activeView === "keepers" && (
            <DraftLabPanel
              initialDraftView="keepers"
              lockedView="keepers"
              preloadedKeepers={preloadedKeepers}
              keepersError={keepersError}
            />
          )}

          {activeView === "draftLab" && <DraftLabPanel initialDraftView="predictive" lockedView="predictive" />}
        </div>
      </div>
    </section>
  );
}

export default function ModernizePreview({ stats = [], rawData = [], rivalries = [], loading }) {
  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--accent-soft)] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="adaptive-stack">
      <AnalyticsPreview stats={stats} rawData={rawData} rivalries={rivalries} />
    </div>
  );
}
